import React, { useState } from 'react';
import { ApiService } from '../../services/api';
import { CloseIcon, MailIcon, CheckIcon } from './Icons';

interface SaveSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
}

export const SaveSessionModal: React.FC<SaveSessionModalProps> = ({ isOpen, onClose, sessionId }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMsg(null);
      const res = await ApiService.sendMagicLink(sessionId, email);
      if (res.success) {
        setSuccessMsg(`Magic resumption link sent to ${email}! Check your inbox to resume on any device.`);
      }
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to send magic link. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div
              style={{
                width: '2.25rem',
                height: '2.25rem',
                borderRadius: '9999px',
                backgroundColor: 'hsl(var(--glow-gemini-blue) / 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'hsl(var(--glow-gemini-blue))'
              }}
            >
              <MailIcon size={18} />
            </div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 700 }}>Save & Resume Consultation</h3>
          </div>
          <button
            type="button"
            className="dock-action-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {successMsg ? (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div
              style={{
                width: '3rem',
                height: '3rem',
                borderRadius: '9999px',
                backgroundColor: 'hsl(var(--success) / 0.2)',
                color: 'hsl(var(--success))',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1rem'
              }}
            >
              <CheckIcon size={24} />
            </div>
            <p style={{ fontSize: '0.9375rem', color: 'hsl(var(--text-primary))', marginBottom: '1.25rem' }}>
              {successMsg}
            </p>
            <button
              type="button"
              className="btn-primary"
              style={{ width: '100%' }}
              onClick={onClose}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p style={{ fontSize: '0.875rem', color: 'hsl(var(--text-secondary))', marginBottom: '1rem', lineHeight: 1.5 }}>
              Receive a secure, 7-day magic link to instantly restore your shortlisted Karachi banquets, budget calculations, and provisional booking holds on any device.
            </p>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.375rem', color: 'hsl(var(--text-muted))' }}>
                Your Email Address
              </label>
              <input
                type="email"
                required
                className="form-input"
                placeholder="name@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {errorMsg && (
              <div
                style={{
                  padding: '0.625rem 0.875rem',
                  borderRadius: '0.5rem',
                  backgroundColor: 'hsl(var(--danger) / 0.15)',
                  border: '1px solid hsl(var(--danger) / 0.4)',
                  color: 'hsl(var(--danger))',
                  fontSize: '0.8125rem',
                  marginBottom: '1rem'
                }}
              >
                {errorMsg}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Magic Link'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
